import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Download, Copy, RotateCcw, Package, CreditCard, Truck, ShoppingCart } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

type BarcodeType = 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'ITF14' | 'MSI' | 'Pharmacode';

interface BarcodeOptions {
  width: number;
  height: number;
  displayValue: boolean;
  fontSize: number;
  margin: number;
  background: string;
  lineColor: string;
}

const BarcodeGenerator: React.FC = () => {
  const [barcodeType, setBarcodeType] = useState<BarcodeType>('CODE128');
  const [barcodeData, setBarcodeData] = useState('');
  const [barcode, setBarcode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [options, setOptions] = useState<BarcodeOptions>({
    width: 2,
    height: 100,
    displayValue: true,
    fontSize: 20,
    margin: 10,
    background: '#FFFFFF',
    lineColor: '#000000'
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useSEO({
    title: 'Barcode Generator - Create Barcodes Online Free',
    description: 'Generate various types of barcodes including CODE128, CODE39, EAN13, UPC, and more. Customize appearance and download as PNG.',
    keywords: 'barcode generator, create barcode, CODE128, EAN13, UPC barcode, product barcode, inventory barcode',
    canonical: 'https://snap-tools.online/security/barcode-generator'
  });

  // Simple barcode generation patterns
  const barcodePatterns = {
    CODE128: {
      // Simplified CODE128 pattern
      startCode: '11010000100',
      stopCode: '1100011101011',
      patterns: {
        '0': '11011001100', '1': '11001101100', '2': '11001100110', '3': '10010011000',
        '4': '10010001100', '5': '10001001100', '6': '10011001000', '7': '10011000100',
        '8': '10001100100', '9': '11001001000', 'A': '11001000100', 'B': '11000100100',
        'C': '10110011100', 'D': '10011011100', 'E': '10011001110', 'F': '10111001000',
        'G': '10011101000', 'H': '10011100100', 'I': '11001110010', 'J': '11001011100',
        'K': '11001001110', 'L': '11011100100', 'M': '11001110100', 'N': '11101101110',
        'O': '11101001100', 'P': '11100101100', 'Q': '11100100110', 'R': '11101100100',
        'S': '11100110100', 'T': '11100110010', 'U': '11011011000', 'V': '11011000110',
        'W': '11000110110', 'X': '10100011000', 'Y': '10001011000', 'Z': '10001000110',
        ' ': '10110001000'
      }
    },
    CODE39: {
      startCode: '1001011011010',
      stopCode: '1001011011010',
      patterns: {
        '0': '101001101101', '1': '110100101011', '2': '101100101011', '3': '110110010101',
        '4': '101001101011', '5': '110100110101', '6': '101100110101', '7': '101001011011',
        '8': '110100101101', '9': '101100101101', 'A': '110101001011', 'B': '101101001011',
        'C': '110110100101', 'D': '101011001011', 'E': '110101100101', 'F': '101101100101',
        'G': '101010011011', 'H': '110101001101', 'I': '101101001101', 'J': '101011001101',
        'K': '110101010011', 'L': '101101010011', 'M': '110110101001', 'N': '101011010011',
        'O': '110101101001', 'P': '101101101001', 'Q': '101010110011', 'R': '110101011001',
        'S': '101101011001', 'T': '101011011001', 'U': '110010101011', 'V': '100110101011',
        'W': '110011010101', 'X': '100101101011', 'Y': '110010110101', 'Z': '100110110101',
        ' ': '100101011011', '-': '100101001011', '.': '110010100101'
      }
    }
  };

  const validateInput = (type: BarcodeType, data: string): boolean => {
    switch (type) {
      case 'CODE128':
        return data.length > 0 && data.length <= 80;
      case 'CODE39':
        return /^[0-9A-Z\-\.\$\/\+\%\s]+$/.test(data) && data.length <= 43;
      case 'EAN13':
        return /^\d{12,13}$/.test(data);
      case 'EAN8':
        return /^\d{7,8}$/.test(data);
      case 'UPC':
        return /^\d{11,12}$/.test(data);
      case 'ITF14':
        return /^\d{13,14}$/.test(data);
      case 'MSI':
        return /^\d+$/.test(data);
      case 'Pharmacode':
        return /^\d{1,6}$/.test(data) && parseInt(data) >= 3 && parseInt(data) <= 131070;
      default:
        return data.length > 0;
    }
  };

  const generateBarcode = async () => {
    if (!barcodeData.trim()) {
      toast.error('Please enter data to generate barcode');
      return;
    }

    if (!validateInput(barcodeType, barcodeData)) {
      toast.error(`Invalid input for ${barcodeType}. Please check the format requirements.`);
      return;
    }

    setIsGenerating(true);

    try {
      const canvas = canvasRef.current;
      if (!canvas) {
        toast.error('Canvas not available');
        setIsGenerating(false);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        toast.error('Canvas context not available');
        setIsGenerating(false);
        return;
      }

      // Calculate dimensions
      const barcodeWidth = barcodeData.length * 12 * options.width + options.margin * 2;
      const textHeight = options.displayValue ? options.fontSize + 10 : 0;
      const totalHeight = options.height + textHeight + options.margin * 2;

      canvas.width = Math.max(barcodeWidth, 300);
      canvas.height = totalHeight;

      // Clear canvas
      ctx.fillStyle = options.background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Generate barcode pattern
      let pattern = '';
      const selectedPattern = barcodePatterns[barcodeType as keyof typeof barcodePatterns];

      if (selectedPattern) {
        pattern += selectedPattern.startCode;
        
        for (const char of barcodeData.toUpperCase()) {
          const charPattern = selectedPattern.patterns[char as keyof typeof selectedPattern.patterns];
          if (charPattern) {
            pattern += charPattern;
          } else {
            // Fallback pattern for unsupported characters
            pattern += '10101010101';
          }
        }
        
        pattern += selectedPattern.stopCode;
      } else {
        // Fallback pattern generation
        pattern = '101';
        for (let i = 0; i < barcodeData.length; i++) {
          const charCode = barcodeData.charCodeAt(i);
          const binaryPattern = (charCode % 2 === 0) ? '110' : '101';
          pattern += binaryPattern;
        }
        pattern += '101';
      }

      // Draw barcode
      ctx.fillStyle = options.lineColor;
      const barWidth = options.width;
      let x = options.margin;
      const y = options.margin;

      for (let i = 0; i < pattern.length; i++) {
        if (pattern[i] === '1') {
          ctx.fillRect(x, y, barWidth, options.height);
        }
        x += barWidth;
      }

      // Draw text if enabled
      if (options.displayValue) {
        ctx.fillStyle = options.lineColor;
        ctx.font = `${options.fontSize}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(
          barcodeData,
          canvas.width / 2,
          options.height + options.margin + options.fontSize
        );
      }

      const dataUrl = canvas.toDataURL('image/png');
      setBarcode(dataUrl);
      toast.success('Barcode generated successfully');

    } catch (error) {
      console.error('Error generating barcode:', error);
      toast.error('Failed to generate barcode');
    }

    setIsGenerating(false);
  };

  const downloadBarcode = () => {
    if (!barcode) {
      toast.error('No barcode to download');
      return;
    }

    const link = document.createElement('a');
    link.download = `barcode-${barcodeType}-${Date.now()}.png`;
    link.href = barcode;
    link.click();
    toast.success('Barcode downloaded');
  };

  const copyBarcode = async () => {
    if (!barcode) {
      toast.error('No barcode to copy');
      return;
    }

    try {
      const response = await fetch(barcode);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      toast.success('Barcode copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy barcode');
    }
  };

  const barcodeTypes = [
    { id: 'CODE128', label: 'CODE 128', description: 'Universal, alphanumeric', icon: Package },
    { id: 'CODE39', label: 'CODE 39', description: 'Alphanumeric, widely used', icon: Truck },
    { id: 'EAN13', label: 'EAN-13', description: 'European, 13 digits', icon: ShoppingCart },
    { id: 'EAN8', label: 'EAN-8', description: 'European, 8 digits', icon: ShoppingCart },
    { id: 'UPC', label: 'UPC-A', description: 'US/Canada, 12 digits', icon: ShoppingCart },
    { id: 'ITF14', label: 'ITF-14', description: 'Shipping containers', icon: Package },
    { id: 'MSI', label: 'MSI', description: 'Inventory management', icon: Package },
    { id: 'Pharmacode', label: 'Pharmacode', description: 'Pharmaceutical', icon: CreditCard },
  ];

  const getFormatHelp = (type: BarcodeType) => {
    switch (type) {
      case 'CODE128':
        return 'Supports letters, numbers, and symbols. Max 80 characters.';
      case 'CODE39':
        return 'Supports 0-9, A-Z, and symbols (- . $ / + % space). Max 43 characters.';
      case 'EAN13':
        return 'Exactly 12 or 13 digits. Example: 123456789012';
      case 'EAN8':
        return 'Exactly 7 or 8 digits. Example: 1234567';
      case 'UPC':
        return 'Exactly 11 or 12 digits. Example: 12345678901';
      case 'ITF14':
        return 'Exactly 13 or 14 digits. Example: 1234567890123';
      case 'MSI':
        return 'Numbers only. Example: 123456';
      case 'Pharmacode':
        return 'Numbers only, 3-131070. Example: 12345';
      default:
        return 'Enter the data to encode in the barcode.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <BarChart3 className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Barcode Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Generate various types of barcodes including CODE128, CODE39, EAN13, UPC, and more for inventory, products, and shipping.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              {/* Barcode Type Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Barcode Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {barcodeTypes.map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setBarcodeType(type.id as BarcodeType)}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          barcodeType === type.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <IconComponent className="w-5 h-5 text-purple-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">{type.label}</div>
                            <div className="text-sm text-gray-500">{type.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Input Form */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Barcode Data</h3>
                <p className="text-sm text-gray-600 mb-3">{getFormatHelp(barcodeType)}</p>
                <input
                  type="text"
                  value={barcodeData}
                  onChange={(e) => setBarcodeData(e.target.value)}
                  placeholder={`Enter ${barcodeType} data...`}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Customization Options */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customization</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bar Width: {options.width}px
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={options.width}
                      onChange={(e) => setOptions({ ...options, width: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height: {options.height}px
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="300"
                      value={options.height}
                      onChange={(e) => setOptions({ ...options, height: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Font Size: {options.fontSize}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="40"
                      value={options.fontSize}
                      onChange={(e) => setOptions({ ...options, fontSize: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Margin: {options.margin}px
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={options.margin}
                      onChange={(e) => setOptions({ ...options, margin: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Color
                    </label>
                    <input
                      type="color"
                      value={options.background}
                      onChange={(e) => setOptions({ ...options, background: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bar Color
                    </label>
                    <input
                      type="color"
                      value={options.lineColor}
                      onChange={(e) => setOptions({ ...options, lineColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={options.displayValue}
                      onChange={(e) => setOptions({ ...options, displayValue: e.target.checked })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Display text below barcode</span>
                  </label>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateBarcode}
                disabled={isGenerating || !barcodeData.trim()}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? 'Generating...' : 'Generate Barcode'}
              </button>
            </div>

            {/* Output Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Generated Barcode</h3>
              
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                {barcode ? (
                  <div className="space-y-4">
                    <img
                      src={barcode}
                      alt="Generated Barcode"
                      className="mx-auto border border-gray-200 rounded-lg max-w-full"
                    />
                    
                    <div className="flex gap-3 justify-center flex-wrap">
                      <button
                        onClick={downloadBarcode}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download PNG
                      </button>
                      
                      <button
                        onClick={copyBarcode}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Copy Image
                      </button>
                      
                      <button
                        onClick={() => setBarcode(null)}
                        className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Clear
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="py-12">
                    <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Your barcode will appear here</p>
                  </div>
                )}
              </div>

              {/* Hidden canvas for barcode generation */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mt-8 mb-6"
          />

          {/* Educational Content */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Barcode Applications</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Retail:</strong> Product identification and pricing</li>
                <li>• <strong>Inventory:</strong> Stock tracking and management</li>
                <li>• <strong>Shipping:</strong> Package tracking and logistics</li>
                <li>• <strong>Manufacturing:</strong> Quality control and production</li>
                <li>• <strong>Healthcare:</strong> Patient safety and medication tracking</li>
                <li>• <strong>Events:</strong> Ticket validation and access control</li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Best Practices</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Choose the right barcode type for your application</li>
                <li>• Ensure sufficient contrast between bars and background</li>
                <li>• Test barcodes with your intended scanner</li>
                <li>• Maintain quiet zones (margins) around barcodes</li>
                <li>• Use appropriate size for scanning distance</li>
                <li>• Verify data accuracy before printing</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BarcodeGenerator;
